import { Component, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { BankStore, ContactStore, InsuranceStore } from '../../core/stores/base.store';
import { Rider, BankTransaction, InitBankTransaction } from '../../core/models';
import { FuseConfirmationService } from '../../core/services/confirmation/confirmation.service';
import { NumberFormatDirective } from '../../core/utils/comma.utils';
import { formatDateAsYYYYMMDD } from '../../core/utils/date.utils';

@Component({
  selector: 'app-rider',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatToolbarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCardModule,
    MatSelectModule,
    MatCheckboxModule,
    NumberFormatDirective
  ],
  templateUrl: './rider.component.html',
  styleUrls: ['./rider.component.css']
})
export class RiderComponent implements OnInit {
  private insuranceStore = inject(InsuranceStore);
  private bankStore = inject(BankStore);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private contactStore = inject(ContactStore);
  
  selectedInsurance = computed(() => this.insuranceStore.selected());
  selectedRider = computed(() => this.insuranceStore.selectedChild());
  banks = computed(() => this.bankStore.entities());
  isEdit = false;
  #fuseConfirmationService = inject(FuseConfirmationService);
  riderForm!: FormGroup;

  // Find the self contact (category = 'Self')
  selfContact = computed(() => {
    return this.contactStore.entities().find(contact => contact.category === 'Self');
  });


  ngOnInit() {     
    if (!this.selectedInsurance()) {
      this.router.navigate(['/insurance']);
    }

    this.riderForm = this.fb.group({
      id: [null],
      insurance_id: [null],
      name: [null],
      face_value: [null, [Validators.required, Validators.min(0)]],
      premium: [null, [Validators.required, Validators.min(0)]],
      cash_value: [null, Validators.min(0)],
      issue_date: [null],
      bank_id: [null],
      createTransaction: [true], // Default to true for convenience
    });

    // Add conditional validation for bank_id based on createTransaction value
    this.riderForm.get('createTransaction')?.valueChanges.subscribe(createTransaction => {
      const bankIdControl = this.riderForm.get('bank_id');
      if (createTransaction) {
        bankIdControl?.setValidators([Validators.required]);
      } else {
        bankIdControl?.clearValidators();
      }
      bankIdControl?.updateValueAndValidity();
    });
    
    // Trigger initial validation based on default createTransaction value
    if (this.riderForm.get('createTransaction')?.value) {
      this.riderForm.get('bank_id')?.setValidators([Validators.required]);
      this.riderForm.get('bank_id')?.updateValueAndValidity();
    }

    if (this.selectedRider()) {
      this.isEdit = true;
      this.riderForm.patchValue(this.selectedRider());
    }
  }

  onSubmit(): void {
    if (this.riderForm.valid) {
      const formValue = this.riderForm.getRawValue();
      
      formValue.issue_date = formatDateAsYYYYMMDD(formValue.issue_date);
      // Convert string values with commas to numbers
      if (typeof formValue.face_value === 'string') {
        formValue.face_value = Number(formValue.face_value.replace(/,/g, ''));
      }
      
      if (typeof formValue.premium === 'string') {
        formValue.premium = Number(formValue.premium.replace(/,/g, ''));
      }
      
      if (formValue.cash_value && typeof formValue.cash_value === 'string') {
        formValue.cash_value = Number(formValue.cash_value.replace(/,/g, ''));
      }
      formValue.insurance_id = this.selectedInsurance()?.id;
      
      // Store the bank_id and createTransaction flag before removing them from the rider object
      const bankId = formValue.bank_id;
      const createTransaction = formValue.createTransaction;
      
      // Remove bank_id and createTransaction from the rider object as they're not part of the rider model
      delete formValue.bank_id;
      delete formValue.createTransaction;
      
      if (this.isEdit) {
        this.insuranceStore.updateChild({
          child: formValue as Rider,
          endpoint: 'rider',
          collectionName: 'riders_collection'
        });
      } else {
        // Set the insurance_id if creating a new rider
        formValue.insurance_id = this.selectedInsurance()?.id;
        
        // Delete the id field when creating a new rider
        delete formValue.id;
        
        this.insuranceStore.createChild({
          child: formValue as Rider,
          endpoint: 'rider',
          collectionName: 'riders_collection'
        });
        
        // If createTransaction is checked and a bank is selected, create a bank transaction
        if (createTransaction && bankId) {
          this.createBankTransaction(formValue as Rider, Number(bankId));
        }
      }
      
      this.router.navigate(['/insurance']);
    }
  }

  onCancel() {
    this.router.navigate(['/insurance']);
  }

  delete() {
    const confirmation = this.#fuseConfirmationService.open({
      title: 'Delete Rider',
      message: 'Are you sure you want to delete this rider? This action cannot be undone!',
      actions: {
        confirm: {
          label: 'Delete'
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.insuranceStore.deleteChild({child:this.selectedRider() as Rider, endpoint: 'rider', collectionName: 'riders_collection'});
        this.router.navigate(['/insurance']);
      }
    })
  }
  
  /**
   * Creates a bank transaction for the rider payment
   * @param rider The rider that was created
   * @param bankId The ID of the bank to create the transaction for
   */
  private createBankTransaction(rider: Rider, bankId: number) {
    if (!rider || !bankId) {
      return;
    }
    
    const transaction: Omit<BankTransaction, 'id'> = {
      ...InitBankTransaction,
      bank_id: bankId,
      contact_id: this.selfContact()?.id,
      loan_id: null,
      insurance_id: rider.insurance_id,
      insurance_loan_id: null,
      category: 'Rider payment',
      amount: -Number(rider.premium),
      principal: 0,
      interest: 0,
      late_fee: 0,
      transaction_date: formatDateAsYYYYMMDD(rider.issue_date)
    };
    delete (transaction as any).id;
    
    // Create the bank transaction
    this.bankStore.createChild({
      child: transaction,
      endpoint: 'bank_transaction',
      collectionName: 'bank_transactions_collection'
    });
  }
}
